import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

interface TripPromotion {
  id: number;
  title: string;
}

interface BookingPromotion {
  id: number;
  date_booking: string;
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  trip_id: number;
  status: string;
  trip_promotion?: TripPromotion;
}

const colors = {
  primary: "#294b5a",
  primaryHover: "#1a3641",
  text: "#1e293b",
  textMuted: "#5f6b76",
  border: "#dbe0e4",
  borderLight: "#e5e7eb",
  bgSubtle: "#f8fafc",
  danger: "#ef4444",
  dangerHover: "#dc2626",
  success: "#10b981",
  warning: "#f59e0b",
  info: "#3b82f6",
  secondary: "#6b7280",
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: `0.5px solid ${colors.borderLight}`,
  borderRadius: 12,
};

const buttonBase: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 500,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  border: "1px solid transparent",
  transition: "all .15s ease",
};

const ListBookingPromotions = () => {
  const [bookingPromotions, setBookingPromotions] = useState<BookingPromotion[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  const fetchBookingPromotions = (page = 1) => {
    axios
      .get(`${API_URL}/booking-promotions?page=${page}`)
      .then((res) => {
        setBookingPromotions(res.data.data);
        if (res.data.last_page) setLastPage(res.data.last_page);
        setCurrentPage(page);
      })
      .catch((error) => {
        console.error("Error fetching booking promotions:", error);
      });
  };

  useEffect(() => {
    fetchBookingPromotions();
  }, []);

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    if (dateString.includes(" ")) return dateString.split(" ")[0];
    if (dateString.includes("T")) return dateString.split("T")[0];
    return dateString;
  };

  const getStatusBadgeStyle = (status: string): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      padding: "4px 8px",
      borderRadius: "6px",
      fontSize: "11px",
      fontWeight: 600,
      color: "#fff",
      display: "inline-block",
      textTransform: "capitalize",
    };

    switch (status) {
      case "pending":
        return { ...baseStyle, backgroundColor: colors.warning };
      case "confirmed":
        return { ...baseStyle, backgroundColor: colors.success };
      case "cancelled":
        return { ...baseStyle, backgroundColor: colors.danger };
      case "completed":
        return { ...baseStyle, backgroundColor: colors.info };
      default:
        return { ...baseStyle, backgroundColor: colors.secondary };
    }
  };

  const formatStatus = (status: string) => {
    const statusTexts: Record<string, string> = {
      pending: "Pending",
      confirmed: "Confirmed",
      cancelled: "Cancelled",
      completed: "Completed",
    };
    return statusTexts[status] || status;
  };

  const handleDeleteClick = (rowIndex: number) => {
    setSelectedRow(rowIndex);
    setShowModal(true);
  };

  const handleCancel = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedRow === null) return;
    try {
      const bookingPromotionToDelete = bookingPromotions[selectedRow];
      await axios.delete(`${API_URL}/booking-trip/${bookingPromotionToDelete.id}`);

      setBookingPromotions((prev) =>
        prev.filter((item) => item.id !== bookingPromotionToDelete.id)
      );
      toast.success("Trip booking deleted successfully");
      fetchBookingPromotions(currentPage);
    } catch (error: any) {
      console.error("Error deleting promotion booking:", error.response || error.message);
    } finally {
      setShowModal(false);
      setSelectedRow(null);
    }
  };

  const handleEditClick = (id: number) => {
    navigate(`/details-booking-promotion/${id}`);
  };

  return (
    <>
      {/* Configuration Grid : Left (20%), Center (60%), Right (20%) */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "20% 60% 20%",
          gap: "16px",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Colonne Gauche (20%) - Filtres & Infos de Synthèse */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ ...cardStyle, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 12px 0", color: colors.text }}>
              Overview
            </h3>
            <div style={{ fontSize: 13, color: colors.textMuted }}>
              Total Bookings: <strong style={{ color: colors.text }}>{bookingPromotions.length}</strong>
            </div>
          </div>

          <div style={{ ...cardStyle, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 8px 0", color: colors.text }}>
              Quick Info
            </h3>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
              Manage promotional offer bookings. Click the action icons to edit details or delete entries.
            </p>
          </div>
        </div>

        {/* Colonne Centre (60%) - Table des Réservations */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Header */}
          <div style={{ ...cardStyle, padding: 16 }}>
            <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0, color: colors.text }}>
              Manage Booking Promotions
            </h1>
            <p style={{ fontSize: 12, color: colors.textMuted, margin: "4px 0 0 0" }}>
              List of all user promotions bookings and current statuses
            </p>
          </div>

          {/* Tableau */}
          <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: colors.bgSubtle, borderBottom: `1px solid ${colors.border}` }}>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: colors.text }}>Promotion</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: colors.text }}>Customer</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: colors.text }}>Email</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: colors.text }}>Date</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: colors.text }}>Status</th>
                    <th style={{ padding: "12px 16px", fontWeight: 600, color: colors.text, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookingPromotions.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: "24px", textAlign: "center", color: colors.textMuted }}>
                        No booking promotions found.
                      </td>
                    </tr>
                  ) : (
                    bookingPromotions.map((item, index) => (
                      <tr
                        key={item.id}
                        style={{
                          borderBottom: `1px solid ${colors.borderLight}`,
                          transition: "background .15s ease",
                        }}
                      >
                        <td style={{ padding: "12px 16px", fontWeight: 500, color: colors.text }}>
                          {item.trip_promotion?.title || "-"}
                        </td>
                        <td style={{ padding: "12px 16px", color: colors.text }}>
                          {item.firstname} {item.lastname}
                        </td>
                        <td style={{ padding: "12px 16px", color: colors.textMuted }}>{item.email}</td>
                        <td style={{ padding: "12px 16px", color: colors.textMuted }}>
                          {formatDate(item.date_booking)}
                        </td>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={getStatusBadgeStyle(item.status)}>
                            {formatStatus(item.status)}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "right" }}>
                          <div style={{ display: "inline-flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button
                              type="button"
                              onClick={() => handleEditClick(item.id)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 2,
                                display: "flex",
                                alignItems: "center",
                              }}
                              title="Edit"
                            >
                              <svg fill={colors.primary} width="18" height="18" viewBox="0 0 512 512">
                                <path d="M320 112L368 64 448 144 400 192 320 112ZM128 304L288 144 368 224 208 384 128 304ZM96 336L176 416 64 448 96 336Z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(index)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: 2,
                                display: "flex",
                                alignItems: "center",
                              }}
                              title="Delete"
                            >
                              <svg fill={colors.danger} width="18" height="18" viewBox="0 0 16 16">
                                <path d="M 6.496094 1 C 5.675781 1 5 1.675781 5 2.496094 L 5 3 L 2 3 L 2 4 L 3 4 L 3 12.5 C 3 13.328125 3.671875 14 4.5 14 L 10.5 14 C 11.328125 14 12 13.328125 12 12.5 L 12 4 L 13 4 L 13 3 L 10 3 L 10 2.496094 C 10 1.675781 9.324219 1 8.503906 1 Z M 6.496094 2 L 8.503906 2 C 8.785156 2 9 2.214844 9 2.496094 L 9 3 L 6 3 L 6 2.496094 C 6 2.214844 6.214844 2 6.496094 2 Z M 5 5 L 6 5 L 6 12 L 5 12 Z M 7 5 L 8 5 L 8 12 L 7 12 Z M 9 5 L 10 5 L 10 12 L 9 12 Z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {bookingPromotions.length > 0 && lastPage > 1 && (
              <div
                style={{
                  padding: "12px 16px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                  borderTop: `1px solid ${colors.borderLight}`,
                  background: colors.bgSubtle,
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (currentPage > 1) fetchBookingPromotions(currentPage - 1);
                  }}
                  disabled={currentPage === 1}
                  style={{
                    ...buttonBase,
                    padding: "6px 10px",
                    background: "#fff",
                    border: `1px solid ${colors.border}`,
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  &larr; Prev
                </button>

                {Array.from({ length: lastPage }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => fetchBookingPromotions(page)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      border: currentPage === page ? "none" : `1px solid ${colors.border}`,
                      background: currentPage === page ? colors.primary : "#fff",
                      color: currentPage === page ? "#fff" : colors.text,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {page}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => {
                    if (currentPage < lastPage) fetchBookingPromotions(currentPage + 1);
                  }}
                  disabled={currentPage === lastPage}
                  style={{
                    ...buttonBase,
                    padding: "6px 10px",
                    background: "#fff",
                    border: `1px solid ${colors.border}`,
                    opacity: currentPage === lastPage ? 0.5 : 1,
                    cursor: currentPage === lastPage ? "not-allowed" : "pointer",
                  }}
                >
                  Next &rarr;
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Colonne Droite (20%) - Widget d'Aide / Actions Complémentaires */}
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ ...cardStyle, padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: "0 0 10px 0", color: colors.text }}>
              Legend
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: colors.warning }} />
                <span>Pending</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: colors.success }} />
                <span>Confirmed</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: colors.danger }} />
                <span>Cancelled</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: colors.info }} />
                <span>Completed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Confirmation de Suppression */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 12,
              width: "420px",
              maxWidth: "90%",
              overflow: "hidden",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
              border: `1px solid ${colors.borderLight}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 18px",
                borderBottom: `1px solid ${colors.borderLight}`,
              }}
            >
              <h5 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.text }}>
                Confirm Deletion
              </h5>
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 18,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: colors.textMuted,
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ padding: "18px", fontSize: 13, color: colors.textMuted, lineHeight: 1.5 }}>
              Are you sure you want to delete this booking promotion? This action cannot be undone.
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                padding: "12px 18px",
                borderTop: `1px solid ${colors.borderLight}`,
                background: colors.bgSubtle,
              }}
            >
              <button
                type="button"
                onClick={handleCancel}
                style={{
                  ...buttonBase,
                  background: "#fff",
                  border: `1px solid ${colors.border}`,
                  color: colors.text,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                style={{
                  ...buttonBase,
                  background: colors.danger,
                  color: "#fff",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default ListBookingPromotions;